/**
 * ConversationsPanel - container that wires the global conversation list into
 * the app chrome (AppSidebar).
 *
 * It owns no state: data + actions come from `useGlobalConversationList`, the
 * active id is read from the URL (`?c=`). This is what makes the conversation
 * list available on every page — selecting one navigates to `/assistant?c=<id>`.
 */

import { useCallback } from "react";
import { toast } from "sonner";
import {
  useSetConversationArchived,
  useSetConversationPinned,
} from "@/hooks/useConversationsAPI";
import { ErrorService } from "@/lib/errors";
import { useGlobalConversationList } from "../../hooks/useGlobalConversationList";
import { GlobalConversationSidebar } from "./GlobalConversationSidebar";

export function ConversationsPanel() {
  const {
    ownedConversations,
    sharedConversations,
    isLoading,
    activeConversationId,
    selectConversation,
    deleteConversation,
    renameConversation,
  } = useGlobalConversationList({ archived: 'include' });
  const { mutateAsync: setConversationPinned } = useSetConversationPinned();
  const { mutateAsync: setConversationArchived } = useSetConversationArchived();

  const handlePinChange = useCallback(async (conversationId: string, pinned: boolean) => {
    try {
      await setConversationPinned({ conversationId, pinned });
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'set_conversation_pinned', conversation_id: conversationId } });
      toast.error(pinned ? "Impossible d'épingler la conversation" : "Impossible de retirer l'épingle");
    }
  }, [setConversationPinned]);

  const handleArchiveChange = useCallback(async (conversationId: string, archived: boolean) => {
    try {
      await setConversationArchived({ conversationId, archived });
    } catch (error) {
      ErrorService.handle(error, { context: { action: 'set_conversation_archived', conversation_id: conversationId } });
      toast.error(archived ? "Impossible d'archiver la conversation" : "Impossible de désarchiver la conversation");
    }
  }, [setConversationArchived]);

  return (
    <GlobalConversationSidebar
      conversations={ownedConversations}
      sharedConversations={sharedConversations}
      activeConversationId={activeConversationId}
      isLoading={isLoading}
      onConversationSelect={selectConversation}
      onConversationDelete={deleteConversation}
      onConversationRename={renameConversation}
      onConversationPinChange={handlePinChange}
      onConversationArchiveChange={handleArchiveChange}
    />
  );
}

export default ConversationsPanel;
